/**
 * Keeps every "how far from free shipping" line in sync with the real cart.
 *
 * The lines are rendered by Liquid, so they are correct when the page loads
 * and then go stale the moment the shopper adds or removes something without
 * a reload. The theme fires `cart:update` on the document for both of those,
 * so we recompute from /cart.js and rewrite the text.
 *
 * Each target carries data-shipping-goal with the threshold in agorot, which
 * comes from snippets/free-shipping-threshold.liquid — the one place the
 * number is defined.
 */

const SELECTOR = '[data-shipping-goal]';

/** @param {number} agorot */
function formatMoney(agorot) {
  const shekels = agorot / 100;
  const text = Number.isInteger(shekels) ? String(shekels) : shekels.toFixed(2);
  return `${text} ₪`;
}

/** @param {HTMLElement} el @param {number} total */
function paint(el, total) {
  const goal = Number(el.dataset.shippingGoal);
  if (!Number.isFinite(goal) || goal <= 0) return;

  const missing = goal - total;
  const reached = missing <= 0;

  const textNode = el.querySelector('.cart-goal__text') || el;
  if (reached) {
    textNode.textContent = el.classList.contains('cart-goal')
      ? 'מעולה, המשלוח עלינו'
      : 'המשלוח עלינו';
  } else {
    const strong = document.createElement('strong');
    strong.className = el.classList.contains('cart-goal') ? 'cart-goal__amount' : '';
    strong.textContent = formatMoney(missing);
    textNode.textContent = '';
    textNode.append('עוד ', strong, el.classList.contains('cart-goal') ? ' ומשלוח חינם' : ' בעגלה ומשלוח חינם');
  }

  el.classList.toggle('cart-goal--reached', reached && el.classList.contains('cart-goal'));

  const fill = el.querySelector('.cart-goal__fill');
  if (fill) {
    const percent = Math.min(100, Math.round((total / goal) * 100));
    fill.style.width = `${percent}%`;
  }
}

async function refresh() {
  const targets = document.querySelectorAll(SELECTOR);
  if (!targets.length) return;

  let total;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;
    ({ total_price: total } = await response.json());
  } catch {
    return; // offline or blocked: leave the server-rendered text alone
  }

  if (typeof total !== 'number') return;
  targets.forEach((el) => paint(/** @type {HTMLElement} */ (el), total));
}

document.addEventListener('cart:update', refresh);
refresh();
